import { z } from 'zod';
import { createPaginatedRequestSchema, createPaginatedResponseSchema } from './pagination.js';

// OfferIdSchema, UserIdSchema, RoleIdSchema are exported for use with branded ID parameters.
// The id field in OfferSchema itself is unbranded z.number() to match
// the drizzle-zod selectOfferSchema output (bigint mode:'number' → z.number()).
const OfferIdSchema = z.number().brand<'OfferId'>();
const UserIdSchema = z.number().int().positive().brand<'UserId'>();
const RoleIdSchema = z.number().int().positive().brand<'RoleId'>();

const OfferDateFields = {
  validUntil: z.coerce.date(),
  defaultDateFrom: z.coerce.date().nullable(),
  defaultDateTo: z.coerce.date().nullable(),
  trashedAt: z.coerce.date().nullable(),
  confirmedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
};

const END_AFTER_START_MESSAGE = 'defaultDateTo must be on or after defaultDateFrom';

function withEndAfterStart<TSchema extends z.ZodTypeAny>(schema: TSchema): TSchema {
  return schema.superRefine((value, ctx) => {
    if (
      typeof value === 'object' &&
      value !== null &&
      'defaultDateFrom' in value &&
      'defaultDateTo' in value
    ) {
      const { defaultDateFrom, defaultDateTo } = value as {
        defaultDateFrom?: Date | null;
        defaultDateTo?: Date | null;
      };

      if (
        defaultDateFrom instanceof Date &&
        defaultDateTo instanceof Date &&
        defaultDateTo < defaultDateFrom
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: END_AFTER_START_MESSAGE,
          path: ['defaultDateTo'],
        });
      }
    }
    // Zod returns ZodEffects from superRefine; keep the input schema type for downstream z.infer users.
  }) as TSchema;
}

// Declaration order is semantically meaningful: it reflects the offer lifecycle
// (draft -> sent -> accepted -> confirmed -> sold -> expired -> cancelled). Consumers rely on
// this order, not just the value set — e.g. the offers list status column sort
// (apps/web/src/features/offers/list/offer-list-page.tsx) sorts by this array's
// index rather than alphabetically. Reorder with care. CANCELLED is reachable from any
// status (including CONFIRMED and SOLD), not just a terminal successor of EXPIRED — its
// position at the end only fixes the list-sort order, it does not imply a required
// transition path. SOLD sits immediately after CONFIRMED (not at the end) because it is
// the step that follows confirmation (campaign-sale / "Sold campaign state").
export const OFFER_STATUSES = [
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'CONFIRMED',
  'SOLD',
  'EXPIRED',
  'CANCELLED',
] as const;

// Queue name constant shared between api and worker packages.
export const OFFER_EXPIRY_SWEEP_QUEUE_NAME = 'offer-expiry-sweep';

const OfferBaseSchema = z.object({
  id: z.number(),
  clientId: z.number(),
  brand: z.string().nullable(),
  title: z.string(),
  status: z.enum(OFFER_STATUSES),
  brandingMode: z.enum(['RW_GROUP', 'WHITE_LABEL']),
  validUntil: z.date(),
  campaignNumber: z.string().nullable(),
  defaultDateFrom: z.date().nullable(),
  defaultDateTo: z.date().nullable(),
  defaultPeriodGranularity: z.enum(['HALF_MONTH', 'MONTH', 'WEEK']).nullable(),
  priceColumnsConfig: z.array(z.unknown()).nullable().optional(),
  notificationEmails: z.array(z.string()).nullable().optional(),
  kikPercentage: z.string(),
  authorId: z.number(),
  salespersonId: z.number().nullable(),
  trashedAt: z.date().nullable(),
  previousStatus: z.enum(OFFER_STATUSES).nullable(),
  // Set once, on the first transition into CONFIRMED, and never cleared —
  // see offer-approval / "Offer Approval Endpoint". Not user-writable (see
  // OfferWritableSchema's omit list below).
  confirmedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const OfferSchema = OfferBaseSchema.extend(OfferDateFields);
export type Offer = z.infer<typeof OfferSchema>;
export const OfferStatusSchema = OfferSchema.shape.status;
export type OfferStatus = z.infer<typeof OfferStatusSchema>;

// The caller's own effective access to an offer — read-only vs. editing.
// Mirrors apps/api/src/offers/offer-access.ts's OfferAccessLevel literal union
// (the single implementation of the access rule); contracts cannot import
// that app-side file, so this is the contracts-owned equivalent the api
// boundary populates from it.
export const OfferAccessLevelSchema = z.enum(['view', 'view+edit']);
export type OfferAccessLevel = z.infer<typeof OfferAccessLevelSchema>;

// Response shape for the single-offer read (GET /offers/:id): OfferSchema plus
// two caller-specific, server-computed fields that have no backing database
// column, so they are deliberately NOT part of OfferSchema/OfferBaseSchema
// itself — adding them there would break the exact-key-match assertion in
// packages/db/src/__tests__/schema/offers-drift.test.ts ("has same set of
// keys" against selectOfferSchema), which OfferSchema is designed to mirror.
// - effectiveAccess: the caller's own access, per the same rule
//   resolveOfferRecordAccess implements — never reveals who else the offer is
//   shared with (offer-draft-foundation / "Offer Read with Access Control").
// - hasUnsecuredAcceptedUnits: whether a confirmed campaign still has
//   accepted nośniki not yet secured by a reservation held for this offer.
//   Always false outside CONFIRMED. Populated by the api boundary, not here.
export const OfferDetailSchema = OfferSchema.extend({
  effectiveAccess: OfferAccessLevelSchema,
  hasUnsecuredAcceptedUnits: z.boolean(),
});
export type OfferDetail = z.infer<typeof OfferDetailSchema>;

const OfferWritableSchema = OfferSchema.omit({
  id: true,
  campaignNumber: true,
  status: true,
  authorId: true,
  trashedAt: true,
  previousStatus: true,
  confirmedAt: true,
  createdAt: true,
  updatedAt: true,
})
  .extend({
    brand: OfferSchema.shape.brand.optional(),
    validUntil: z.coerce.date().optional(),
    defaultDateFrom: z.coerce.date().nullable().optional(),
    defaultDateTo: z.coerce.date().nullable().optional(),
  })
  .strict();

export const CreateOfferSchema = withEndAfterStart(
  OfferWritableSchema.extend({
    // The caller no longer supplies the offer's name: it is generated at
    // creation from the campaign number (offer-draft-numbering). Optional
    // rather than omitted so a still-running previous release that sends one
    // is tolerated, not rejected (offer-draft-foundation / "Caller-supplied
    // name is ignored rather than rejected") — the api layer discards
    // whatever value arrives.
    title: OfferWritableSchema.shape.title.optional(),
    brandingMode: OfferWritableSchema.shape.brandingMode.optional(),
    defaultPeriodGranularity: OfferWritableSchema.shape.defaultPeriodGranularity.optional(),
    kikPercentage: OfferWritableSchema.shape.kikPercentage.optional(),
    notificationEmails: OfferWritableSchema.shape.notificationEmails.optional(),
    priceColumnsConfig: OfferWritableSchema.shape.priceColumnsConfig.optional(),
    // Fresh, non-nullable optional — NOT derived from OfferWritableSchema.shape.salespersonId
    // (that shape is nullable; chaining .optional() onto it would infer
    // number | null | undefined, letting an explicit `salespersonId: null` slip past
    // validation and reach the api-boundary's `=== undefined` required-field check as
    // null instead of undefined).
    salespersonId: z.number().optional(),
  }),
);
export type CreateOffer = z.infer<typeof CreateOfferSchema>;

export const UpdateOfferSchema = withEndAfterStart(
  OfferWritableSchema.omit({ salespersonId: true }).partial().extend({
    updatedAt: z.coerce.date(),
  }),
);
export type UpdateOffer = z.infer<typeof UpdateOfferSchema>;

// POST /offers/:id/cancel body. Reason length bounds match SetOfferStatusBodySchema's
// established reason field (see offer-approval.endpoints.ts).
export const CancelOfferSchema = z
  .object({
    reason: z.string().min(5).max(500),
  })
  .strict();
export type CancelOffer = z.infer<typeof CancelOfferSchema>;

// Shared with the offer-draft-numbering generator (apps/api/src/offers/campaign-number-generator.ts):
// the composer caps a generated number to this same length by shortening the
// brand element, so every generated number can be re-saved unchanged through
// the manual edit path below (offer-draft-numbering / "Long brand is
// shortened to keep the number re-savable").
export const CAMPAIGN_NUMBER_MAX_LENGTH = 64;

// PATCH /offers/:id/campaign-number body. No format/prefix is enforced — the
// initials-and-month departmental convention is deliberately not policed by the system.
export const UpdateCampaignNumberSchema = z
  .object({
    campaignNumber: z.string().trim().min(1).max(CAMPAIGN_NUMBER_MAX_LENGTH),
    updatedAt: z.coerce.date(),
  })
  .strict();
export type UpdateCampaignNumber = z.infer<typeof UpdateCampaignNumberSchema>;

// Cursor-paginated (CU-86cb4wjwk): the server slices its full result set to a
// bounded page via sliceOffsetPage — see OffersService.list. Reuses the same
// offset-encoded cursor convention as offer-pricing/offer-availability.
export const ListOffersQuerySchema = createPaginatedRequestSchema(100)
  .extend({
    view: z.enum(['mine', 'shared-with-me', 'all']).default('mine'),
    // Additive filter: narrows the 'all' view to specific authors. Only takes
    // effect when the caller passes the same offer.view_all check used by 'all'
    // (see OffersService.list); otherwise it is silently ignored server-side.
    assignedToUserIds: z.array(z.coerce.number().int().positive()).optional(),
  })
  .strict();
export type ListOffersQuery = z.infer<typeof ListOffersQuerySchema>;

// Response item shape for GET /offers (list): OfferSchema plus the caller's own
// effectiveAccess for that row. Mirrors OfferDetailSchema's pattern above — a
// response-only extension of OfferSchema rather than a field on OfferSchema
// itself, since effectiveAccess has no backing column (see
// packages/db/AGENTS.md's "MUST NOT add a column-less field..." gotcha).
// Lets the offer list exclude a selected ACCEPTED row from the bulk
// withdraw-acceptance payload up front when the caller only holds 'view'
// access to it, instead of only discovering the FORBIDDEN rejection after
// submitting (client-offer-decision-persistence / "Bulk withdrawal surface
// in the internal offer list").
export const OfferListItemSchema = OfferSchema.extend({
  effectiveAccess: OfferAccessLevelSchema,
});
export type OfferListItem = z.infer<typeof OfferListItemSchema>;

export const OfferListResponseSchema = createPaginatedResponseSchema(OfferListItemSchema);
export type OfferListResponse = z.infer<typeof OfferListResponseSchema>;

// GET /offers/campaign-number-preview query params (offer-draft-numbering /
// "Campaign-number preview on the offer creation form"). clientId is a plain
// coerced number (query-string values arrive as strings) rather than
// ClientIdSchema, matching ListOffersQuerySchema's assignedToUserIds
// precedent for numeric GET params. acronym and salespersonName are passed
// through as-is by the caller, which already holds both from its own
// client/salesperson list caches — the preview endpoint composes with them
// rather than re-resolving them from clientId/salespersonId, so it costs a
// single query (the ordinal peek) rather than three.
export const CampaignNumberPreviewQuerySchema = z
  .object({
    clientId: z.coerce.number().int().positive(),
    brand: z.string().trim().min(1).optional(),
    acronym: z.string().trim().min(1),
    salespersonName: z.string().trim().min(1),
  })
  .strict();
export type CampaignNumberPreviewQuery = z.infer<typeof CampaignNumberPreviewQuerySchema>;

// Response for the campaign-number preview endpoint: the full composed
// number the draft would receive if created at that moment, including the
// ordinal. Not reserved — see the requirement above for the accepted
// staleness this implies.
export const CampaignNumberPreviewSchema = z.object({
  campaignNumber: z.string(),
});
export type CampaignNumberPreview = z.infer<typeof CampaignNumberPreviewSchema>;

// Minimal user projection for the offers-list "assigned to" picker.
// Intentionally excludes email/role/department. Id and display name only.
export const OfferAssigneeSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type OfferAssignee = z.infer<typeof OfferAssigneeSchema>;

// Minimal user projection for the sales-department salesperson picker
// (offer-salesperson-assignment). Same shape as OfferAssigneeSchema — id and
// display name only — but kept as its own schema/type since it represents a
// different, department-scoped list (GET /offers/sales-users), not the
// offers-list "assigned to" filter.
export const SalesUserSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type SalesUser = z.infer<typeof SalesUserSchema>;

// Minimal user projection for the offer sharing dialog's user picker
// (offer-sharing-picker). Same shape as OfferAssigneeSchema/SalesUserSchema —
// id and display name only — kept as its own schema since it backs a
// different, per-offer-scoped endpoint (GET /offers/:id/shareable-users).
export const ShareableUserSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type ShareableUser = z.infer<typeof ShareableUserSchema>;

// Minimal role projection for the offer sharing dialog's role picker
// (GET /offers/:id/shareable-roles). Id and display name only.
export const ShareableRoleSchema = z.object({
  id: z.number(),
  name: z.string(),
});
export type ShareableRole = z.infer<typeof ShareableRoleSchema>;

const OfferShareSelectSchema = z.object({
  id: z.number(),
  offerId: z.number(),
  granteeType: z.enum(['user', 'role']),
  granteeUserId: z.number().nullable(),
  granteeRoleId: z.number().nullable(),
  permission: z.enum(['view', 'view+edit']),
  grantedBy: z.number(),
  createdAt: z.date(),
});

const OfferShareBaseSchema = OfferShareSelectSchema.omit({
  id: true,
  offerId: true,
  grantedBy: true,
  createdAt: true,
  granteeUserId: true,
  granteeRoleId: true,
}).strict();

const UserOfferShareSchema = OfferShareBaseSchema.extend({
  granteeType: z.literal('user'),
  granteeUserId: UserIdSchema,
}).strict();

const RoleOfferShareSchema = OfferShareBaseSchema.extend({
  granteeType: z.literal('role'),
  granteeRoleId: RoleIdSchema,
}).strict();

export const OfferShareSchema = z.discriminatedUnion('granteeType', [
  UserOfferShareSchema,
  RoleOfferShareSchema,
]);
export type OfferShare = z.infer<typeof OfferShareSchema>;

export const OfferShareRecordSchema = OfferShareSelectSchema.extend({
  createdAt: z.coerce.date(),
});
export type OfferShareRecord = z.infer<typeof OfferShareRecordSchema>;

export const UpdateOfferSharesSchema = z
  .object({
    shares: z.array(OfferShareSchema),
  })
  .strict();
export type UpdateOfferShares = z.infer<typeof UpdateOfferSharesSchema>;

export { END_AFTER_START_MESSAGE, OfferIdSchema, RoleIdSchema, UserIdSchema };
